import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  Box,
  Input,
  Button,
  InputGroup,
  InputRightElement,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Link,
} from "@chakra-ui/react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import AmplifyService from "../../services/amplify";
import { bookingFlowStore } from "../../store/BookingFlowStore";
import * as Sentry from "@sentry/react";
import CreateUserStep from "../CreateUserStep";
import { useAlert } from "../AlertProvider";
import {
  useUserQuery,
  usePatientLazyQuery,
} from "../../graphql/types-and-hooks";
import { useBookAppointmentStore } from "../../store";
import { servicebookingFlowStore } from "../../store/ServiceBookingFlowStore";
import { patientStore } from "../../store/PatientStore";
import { observer } from "mobx-react"; // Add this import

const loginService = new AmplifyService();

const generateReferenceCode = () => Math.random().toString(36).substring(2, 15);

const logError = (error: any, errorInfo: any = null) => {
  const referenceCode = generateReferenceCode();
  Sentry.captureException(error, { extra: { ...errorInfo, referenceCode } });
  console.error(
    `Error logged with reference code: ${referenceCode}`,
    error,
    errorInfo
  );
  return referenceCode;
};

logError.info = (message: string, info: any) => console.info(message, info);
logError.error = (message: string, error: any) =>
  logError(new Error(message), { error });

// Wrap the component with observer to make it reactive to MobX changes
const Step8_Login: React.FC = observer(() => {
  const { refetch: refetchUser } = useUserQuery();
  const MAINP = 0,
    LOGIN = 1,
    MFA = 2,
    RESET = 3,
    NEWPASSWORD = 4;
  const [viewState, setViewState] = useState(LOGIN);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [MFAValue, setMFAValue] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn] = useState(false);
  const [showLoginError, setShowLoginError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [buttonText, setButtonText] = useState("Login");
  const [resetMessage, setResetMessage] = useState("");
  const [showUserCreateModal, setShowUserCreateModal] = useState(false);
  const { showAlert } = useAlert();
  const { showAlert: showPopUpAlert } = useAlert();

  const [user, setUser] = useState({});

  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  
  // Access store values in component render phase is now safe with observer
  const buttonBgColor = isServiceBooking && servicebookingFlowStore.configurationDetails?.colorScheme?.buttonBg
    ? servicebookingFlowStore.configurationDetails.colorScheme.buttonBg
    : "#B92F2F";

  useEffect(() => {
    setViewState(LOGIN);
  }, []);

  const handleCreateAccount = () => setShowUserCreateModal(true);

  // eslint-disable-next-line
  const [patient, doctor, setData, resetData] = useBookAppointmentStore(
    (state) => [state.patient, state.doctor, state.setData, state.resetData]
  );

  // eslint-disable-next-line
  const handleLoginAgain = () => {
    setShowLoginError(false);
    setMFAValue("");
    setLoginError("");
    setViewState(viewState === MFA ? LOGIN : RESET);
  };

  const handleLogin = async () => {
    if (viewState === MAINP) {
      setViewState(LOGIN);
      setButtonText("Login");
    } else {
      switch (viewState) {
        case LOGIN:
          doLogin();
          break;
        case MFA:
          doMFA();
          break;
        case RESET:
          doPasswordReset();
          break;
        case NEWPASSWORD:
          doNewPassword();
          break;
      }
    }
  };
  // Fetch patient data when user is available
  const [getPatient] = usePatientLazyQuery();
  const doLogin = () => {
    loginService
      .signIn({
        username: email,
        password: password,
      })
      .then((result: any) => {
        if (result) {
          setUser(result);
          switch (result.challengeName) {
            case "SOFTWARE_TOKEN_MFA":
            case "SMS_MFA":
              setViewState(MFA);
              setEmail("");
              setPassword("");
              setShowLoginError(false);
              setLoginError("");
              setButtonText("Confirm");
              break;
            default:
            case "DONE":
              bookingFlowStore.setCurrentStep(8);
              bookingFlowStore.nextStep();
              servicebookingFlowStore.setActiveStep(6);
              // servicebookingFlowStore.nextStep();
              break;
            case "NEW_PASSWORD_REQUIRED": {
              showAlert({
                severity: "info",
                title: "New Password Required",
                message:
                  "You need to log into the web app to reset your password",
              });
            }
          }
        }
      })
      .catch((e) => {
        if (
          e.code === "NotAuthorizedException" &&
          e.message === "Incorrect username or password."
        ) {
          setLoginError("Incorrect username or password.");
        } else {
          const referenceCode = logError(e);
          setLoginError(
            `An error occurred. Please contact support with reference code: ${referenceCode}`
          );
        }

        setShowLoginError(true);
      });
  };

  const doMFA = async () => {
    if (!MFAValue) {
      setShowLoginError(true);
      setLoginError("Confirmation code cannot be empty");
      return;
    }

    const userAttributes = {};

    try {
      const signInResult = await loginService.confirmSignIn(MFAValue, user, {
        userAttributes,
      });

      if (signInResult.signInUserSession) {
        setButtonText("Login");
        setMFAValue("");
        setEmail("");
        setPassword("");
        setShowLoginError(false);
        setLoginError("");

        const loginUser = await refetchUser();

        console.log("User data", loginUser.data.user);
        const patientUser = loginUser.data.user;
        const patientData = await getPatient({
          variables: {
            input: {
              clinic_group_crm: doctor.clinic_group_crm,
              user_fk: doctor.user_fk,
              location_id: doctor.location_id,
              dob: patientUser.dob,
              first_name: patientUser.first_name,
              last_name: patientUser.last_name,
              contact_number: patientUser.phone_number,
            },
          },
        });

        const patientInfo = {
          id: patientData?.data?.patient?.patient_id,
          type: "myself",
          first_name: loginUser.data.user.first_name,
          last_name: loginUser.data.user.last_name,
          dob: loginUser.data.user.dob,
          contact_number: loginUser.data.user.phone_number,
          is_new_pt: patientData?.data?.patient?.is_new_pt,
          last_seen_did: patientData?.data?.patient?.last_seen_did,
        };

        patientStore.setCurrentPatient(patientInfo);
        console.log("patientInfo", patientInfo);

        const isForFamilyMember = servicebookingFlowStore.isServiceBooking
          ? !servicebookingFlowStore.appointmentData.isForUser
          : !bookingFlowStore.appointmentData.isForUser;
        const familyMember = servicebookingFlowStore.isServiceBooking
          ? servicebookingFlowStore.appointmentData.selectedFamilyMember
          : bookingFlowStore.appointmentData.selectedFamilyMember;

        const isNewPatient = (patient?.is_new_pt ?? "1") === "1";

        const myPatientsOnly = doctor?.my_patients === "1";
        const isExistingPatientOfDoctor =
          patient?.last_seen_did === doctor?.doctor_id;

        if (
          !isServiceBooking &&
          typeof bookingFlowStore.previousStep === "number" &&
          bookingFlowStore.previousStep !== 6 &&
          bookingFlowStore.previousStep !== 7
        ) {
          bookingFlowStore.returnToPreviousStep();
          console.log(bookingFlowStore.previousStep);
          console.log("Returned to previous step (normal booking)");
        } else if (
          isServiceBooking &&
          typeof servicebookingFlowStore.previousStep === "number" &&
          servicebookingFlowStore.previousStep !== 2 &&
          servicebookingFlowStore.previousStep !== 3
        ) {
          servicebookingFlowStore.prevStep();
          console.log(servicebookingFlowStore.previousStep);
          console.log("Returned to previous step (service booking)");
        } else if (
          (servicebookingFlowStore.appointmentData.appointmentType.new_patient ?? '0') === "0" &&
          isNewPatient
        ) {
          showPopUpAlert({
            severity: "info",
            title: "whoops",
            message:
              "This appointment type is not available for new patients. Please select a different appointment type.",
          });
          bookingFlowStore.updateAppointmentData("serviceType", null);
          bookingFlowStore.updateAppointmentData("appointmentType", null);
          bookingFlowStore.updateAppointmentData("selectedDoctor", null);
          bookingFlowStore.updateAppointmentData("selectedTime", null);
          bookingFlowStore.setActiveStep(2);
          bookingFlowStore.goToStep(3);

          servicebookingFlowStore.updateAppointmentData("serviceType", null);
          servicebookingFlowStore.updateAppointmentData(
            "appointmentType",
            null
          );
          servicebookingFlowStore.updateAppointmentData("selectedDoctor", null);
          servicebookingFlowStore.updateAppointmentData("selectedTime", null);
          servicebookingFlowStore.setActiveStep(2);
          servicebookingFlowStore.goToStep(3);
          console.log("service booking 3");
        } else if (doctor?.no_new_patients === "1" && isNewPatient) {
          showPopUpAlert({
            severity: "info",
            title: "whoops",
            message:
              "This doctor is not available for new patients. Please select a different doctor.",
          });
          bookingFlowStore.updateAppointmentData("selectedDoctor", null);
          bookingFlowStore.updateAppointmentData("selectedTime", null);
          bookingFlowStore.setActiveStep(5);
          bookingFlowStore.goToStep(6);

          servicebookingFlowStore.updateAppointmentData("selectedDoctor", null);
          servicebookingFlowStore.updateAppointmentData("selectedTime", null);
          servicebookingFlowStore.setActiveStep(1);
          servicebookingFlowStore.goToStep(2);
          console.log("service booking 2");
        } else if (myPatientsOnly && !isExistingPatientOfDoctor) {
          showPopUpAlert({
            severity: "info",
            title: "whoops",
            message:
              "This doctor is not available for new patients. Please select a different doctor.",
          });
          bookingFlowStore.updateAppointmentData("selectedDoctor", null);
          bookingFlowStore.updateAppointmentData("selectedTime", null);
          bookingFlowStore.setActiveStep(5);
          bookingFlowStore.goToStep(6);

          servicebookingFlowStore.updateAppointmentData("selectedDoctor", null);
          servicebookingFlowStore.updateAppointmentData("selectedTime", null);
          servicebookingFlowStore.setActiveStep(1);
          servicebookingFlowStore.goToStep(2);
          console.log("service booking 2");
        } else if (isForFamilyMember && !familyMember) {
          console.log("Family member not selected");
          bookingFlowStore.goToStep(9);
          servicebookingFlowStore.goToStep(5);

        } else {
          console.log("service booking 6");
          bookingFlowStore.goToStep(10);
          servicebookingFlowStore.goToStep(6);
        }
      } else {
        const err = signInResult.error.message;
        setLoginError(err);
        setShowLoginError(true);
      }
    } catch (error) {
      //console.error("MFA Error:", error);
      setLoginError("MFA code not correct");
      setShowLoginError(true);
    }
  };

  const onForgotPassword = () => {
    setViewState(RESET);
    setButtonText("Submit");
  };

  const doPasswordReset = () => {
    loginService
      .forgotPassword(email)
      .then((result: any) => {
        setViewState(NEWPASSWORD);
        setButtonText("Return");
        setResetMessage(
          `Reset email sent to ${result.CodeDeliveryDetails.Destination}`
        );
      })
      .catch(() => {
        setLoginError("Failed to send reset email.");
        setShowLoginError(true);
      });
  };

  const doNewPassword = () => {
    setViewState(LOGIN);
    setButtonText("Submit");
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
    >
      <Box flex="1" maxWidth="400px" minWidth="320px">
        {viewState === MAINP && !showUserCreateModal && (
          <Text fontSize="md" textAlign="center">
            Select your choice to login your account.
          </Text>
        )}

        {viewState === LOGIN && !showUserCreateModal && (
          <>
            <Text fontSize="md" textAlign="center">
              Enter your details to login.
            </Text>
            <FormControl mb={4} mt={8}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                borderRadius="30px"
                width="100%"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Password</FormLabel>
              <InputGroup size="md">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  borderRadius="30px"
                  width="100%"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleLogin();
                    }
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    borderRadius="30px"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <Link alignSelf="center" onClick={onForgotPassword}>
              {" "}
              Forgot password{" "}
            </Link>
          </>
        )}

        {viewState === MFA && !showUserCreateModal &&(
          <>
            <Text fontSize="md" textAlign="center">
              Please enter the verification code we just sent to your mobile number via SMS. This helps keep your account secure with multi-factor authentication (MFA).
            </Text>
            <FormControl mb={4} mt={8}>
              <FormLabel>Code</FormLabel>
              <Input
                type="number"
                value={MFAValue}
                onChange={(e) => setMFAValue(e.target.value)}
                placeholder="Enter MFA Code"
                borderRadius="30px"
                width="100%"
              />
            </FormControl>
          </>
        )}

        {viewState === RESET && !showUserCreateModal &&(
          <>
            <Text fontSize="md">
              Enter the details below to reset to your password.
            </Text>
            <FormControl mb={4} mt={8}>
              <FormLabel>Email</FormLabel>
              <Input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                placeholder="Enter your email"
                borderRadius="30px"
              />
            </FormControl>
          </>
        )}

        {viewState === NEWPASSWORD && !showUserCreateModal &&(
          <>
            <Text fontSize="md">{resetMessage}</Text>
          </>
        )}

        {showLoginError && !showUserCreateModal && (
          <Alert status="error" mb={4} mt={4}>
            <AlertIcon />
            {loginError}
          </Alert>
        )}

        {!showUserCreateModal && (
          <Button
            bg={buttonBgColor} // Use the computed value
            color="#fff"
            width="100%"
            borderRadius="30px"
            onClick={handleLogin}
            isLoading={isLoggingIn}
            isDisabled={
              (!email.trim() || !password.trim()) && viewState === LOGIN
            }
            mt={4}
          >
            {buttonText}
          </Button>
        )}

        {!showUserCreateModal && (
          <Text fontSize="md" mt={5} textAlign="center">
            or
          </Text>
        )}

        {!showUserCreateModal && (
          <Button
            variant="outline"
            borderWidth="2px"
            borderColor={buttonBgColor} // Use the computed value
            color={buttonBgColor} // Use the computed value
            width="100%"
            borderRadius="30px"
            onClick={handleCreateAccount}
            mt={4}
            mb={4}
            _hover={{ bg: buttonBgColor, color: "white" }} // Use the computed value
          >
            Create an Account
          </Button>
        )}
      </Box>

      {showUserCreateModal && <CreateUserStep />}
    </Flex>
  );
});

export default Step8_Login;